import type { Lang } from '@/lib/design/types'

type SlideStrings = {
    swipe: string
    ctaTitle: string
    ctaBody: (title: string) => string
    ctaButton: string
    saveLine: string
    keyPoints: string
    readMore: string
    takeaways: (count: number, title: string) => string
    swipeSubtitle: string
}

const STRINGS: Record<Lang, SlideStrings> = {
    en: {
        swipe: 'Swipe',
        ctaTitle: 'Want the *full story*?',
        ctaBody: title => `Read "${title}" through the link in bio, and save this post for later.`,
        ctaButton: 'Follow for more',
        saveLine: 'Save this for later and share it with someone who needs it.',
        keyPoints: 'Inside:',
        readMore: 'Read the full post through the link in bio.',
        takeaways: (count, title) => `${count} takeaways from "${title}"`,
        swipeSubtitle: 'Swipe for the key points'
    },
    pt: {
        swipe: 'Arraste',
        ctaTitle: 'Quer a *história completa*?',
        ctaBody: title => `Leia "${title}" pelo link na bio e salve este post para depois.`,
        ctaButton: 'Siga para mais',
        saveLine: 'Salve para depois e compartilhe com quem precisa.',
        keyPoints: 'Neste post:',
        readMore: 'Leia o artigo completo pelo link na bio.',
        takeaways: (count, title) => `${count} lições de "${title}"`,
        swipeSubtitle: 'Arraste para ver os pontos principais'
    },
    es: {
        swipe: 'Desliza',
        ctaTitle: '¿Quieres la *historia completa*?',
        ctaBody: title => `Lee "${title}" desde el enlace de la bio y guarda este post para después.`,
        ctaButton: 'Síguenos para más',
        saveLine: 'Guárdalo para después y compártelo con quien lo necesite.',
        keyPoints: 'En este post:',
        readMore: 'Lee el artículo completo desde el enlace de la bio.',
        takeaways: (count, title) => `${count} claves de "${title}"`,
        swipeSubtitle: 'Desliza para ver las claves'
    },
    fr: {
        swipe: 'Glissez',
        ctaTitle: 'Envie de *tout lire* ?',
        ctaBody: title => `Lisez « ${title} » via le lien en bio et enregistrez ce post pour plus tard.`,
        ctaButton: 'Suivez pour plus',
        saveLine: 'Enregistrez-le et partagez-le avec quelqu’un qui en a besoin.',
        keyPoints: 'Au programme :',
        readMore: 'Lisez l’article complet via le lien en bio.',
        takeaways: (count, title) => `${count} leçons de « ${title} »`,
        swipeSubtitle: 'Glissez pour les points clés'
    },
    de: {
        swipe: 'Wischen',
        ctaTitle: 'Willst du *alles lesen*?',
        ctaBody: title => `Lies „${title}“ über den Link in der Bio und speichere diesen Beitrag.`,
        ctaButton: 'Folge für mehr',
        saveLine: 'Speichere es für später und teile es mit jemandem, der es braucht.',
        keyPoints: 'Darin:',
        readMore: 'Den ganzen Artikel findest du über den Link in der Bio.',
        takeaways: (count, title) => `${count} Erkenntnisse aus „${title}“`,
        swipeSubtitle: 'Wische für die wichtigsten Punkte'
    },
    it: {
        swipe: 'Scorri',
        ctaTitle: 'Vuoi la *storia completa*?',
        ctaBody: title => `Leggi "${title}" dal link in bio e salva questo post per dopo.`,
        ctaButton: 'Seguici per altro',
        saveLine: 'Salvalo per dopo e condividilo con chi ne ha bisogno.',
        keyPoints: 'Nel post:',
        readMore: 'Leggi l’articolo completo dal link in bio.',
        takeaways: (count, title) => `${count} lezioni da "${title}"`,
        swipeSubtitle: 'Scorri per i punti chiave'
    }
}

export const LANGS: Array<Lang> = ['en', 'pt', 'es', 'fr', 'de', 'it']

export const toLang = (value: string | null | undefined): Lang => {
    const code = (value || '').toLowerCase().slice(0, 2)

    return (LANGS as Array<string>).includes(code) ? code as Lang : 'en'
}

export const slideStrings = (lang: Lang): SlideStrings => STRINGS[lang] ?? STRINGS.en
